import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';
import { ukPhonemes, usPhonemes } from './data/phonemes';
import { PronunciationType } from './types';
import AudioRecorder from './components/AudioRecorder';
import AudioAnalyzer from './components/AudioAnalyzer';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { alpha } from '@mui/material/styles';

interface PhonemeCardState {
  recordedAudio: Blob | null;
  score: number | null;
  feedback: string | null;
  pronunciationType: PronunciationType;
}

function App() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [pronunciationType, setPronunciationType] = useState<PronunciationType>('uk');
  const [phonemeStates, setPhonemeStates] = useState<Record<string, PhonemeCardState>>({});

  const phonemes = pronunciationType === 'uk' ? ukPhonemes : usPhonemes;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handlePronunciationTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: PronunciationType | null
  ) => {
    if (newValue !== null) {
      // 清除所有录音状态
      setPhonemeStates({});
      // 更新发音类型
      setPronunciationType(newValue);
      
      // 确保所有音频都停止播放
      const audioElements = document.getElementsByTagName('audio');
      Array.from(audioElements).forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    }
  };

  // 播放标准音标发音
  const playStandardAudio = async (audioPath: string) => {
    try {
      // 每次播放创建新的 AudioContext，避免状态问题
      const audioContext = new AudioContext();
      console.log('Playing standard audio:', audioPath);

      const response = await fetch(audioPath);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      
      // 播放结束后关闭 AudioContext
      source.onended = async () => {
        await audioContext.close();
      };

      source.start(0);
    } catch (error) {
      console.error('Error playing standard audio:', error);
    }
  };

  const handleRecordingComplete = (symbol: string, audioBlob: Blob) => {
    const stateKey = `${pronunciationType}_${symbol}`;
    setPhonemeStates(prev => ({
      ...prev,
      [stateKey]: {
        recordedAudio: audioBlob,
        score: null,
        feedback: null,
        pronunciationType,
      }
    }));
  };

  const handleAnalysisComplete = (symbol: string, score: number, feedback: string) => {
    const stateKey = `${pronunciationType}_${symbol}`;
    setPhonemeStates(prev => ({
      ...prev,
      [stateKey]: {
        ...prev[stateKey],
        score,
        feedback,
      }
    }));
  };

  const renderPhonemeCards = (phonemeList: Array<{ symbol: string; example: string; audio: string; description: string }>) => {
    return (
      <Grid container spacing={3} sx={{ px: 2, py: 1 }}>
        {phonemeList.map(({ symbol, example, audio, description }) => {
          const stateKey = `${pronunciationType}_${symbol}`;
          const state = phonemeStates[stateKey] || { recordedAudio: null, score: null, feedback: null, pronunciationType };
          
          const effectiveState = state.pronunciationType === pronunciationType ? state : { 
            recordedAudio: null, 
            score: null, 
            feedback: null, 
            pronunciationType 
          };
          
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={stateKey}>
              <Card 
                elevation={0}
                sx={{
                  borderRadius: 2,
                  transition: 'all 0.3s ease-in-out',
                  background: '#faf7f2',
                  border: '2px solid #2c1810',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '4px 4px 0 #2c1810',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    right: -8,
                    bottom: -8,
                    background: 'repeating-linear-gradient(45deg, #2c1810, #2c1810 2px, transparent 2px, transparent 8px)',
                    zIndex: -1,
                    opacity: 0.1,
                  }
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box>
                    <Typography 
                      variant="h4" 
                      component="div"
                      sx={{ 
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: 700,
                        color: '#2c1810',
                        mb: 1,
                        fontSize: '2.5rem',
                        textAlign: 'center'
                      }}
                    >
                      {symbol}
                    </Typography>
                    <Typography 
                      sx={{ 
                        fontSize: '1.1rem',
                        fontStyle: 'italic',
                        mb: 1,
                        color: '#2c1810',
                        textAlign: 'center',
                        fontFamily: '"Playfair Display", serif',
                      }}
                    >
                      {example}
                    </Typography>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: '0.9rem',
                        color: '#2c1810',
                        opacity: 0.8,
                        textAlign: 'center',
                        fontFamily: '"Playfair Display", serif',
                      }}
                    >
                      {description}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ 
                    my: 2,
                    borderColor: '#2c1810',
                    opacity: 0.2,
                    borderStyle: 'dashed'
                  }} />
                  
                  <Box sx={{ mt: 2 }}>
                    <Stack 
                      direction="row" 
                      spacing={2} 
                      alignItems="center"
                      justifyContent="center"
                    >
                      <IconButton
                        onClick={() => playStandardAudio(audio)}
                        size="medium"
                        sx={{
                          border: '2px solid #2c1810',
                          color: '#2c1810',
                          p: 1,
                          '&:hover': {
                            backgroundColor: '#2c1810',
                            color: '#e8dcc4',
                          }
                        }}
                      >
                        <VolumeUpIcon />
                      </IconButton>

                      <AudioRecorder
                        onRecordingComplete={(blob) => handleRecordingComplete(symbol, blob)}
                      />
                    </Stack>
                  </Box>
                  
                  {effectiveState.recordedAudio && (
                    <Box sx={{ 
                      mt: 2,
                      pt: 2,
                      borderTop: '2px dashed #2c1810',
                      borderColor: 'rgba(44, 24, 16, 0.2)'
                    }}>
                      <AudioAnalyzer
                        recordedAudio={effectiveState.recordedAudio}
                        targetAudio={audio}
                        onAnalysisComplete={(score, feedback) => 
                          handleAnalysisComplete(symbol, score, feedback)}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ 
      py: 4,
      minHeight: '100vh',
      background: '#2c1810',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `repeating-linear-gradient(
          -45deg,
          #2c1810,
          #2c1810 10px,
          #3a2116 10px,
          #3a2116 40px
        )`,
        opacity: 0.7,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `repeating-linear-gradient(
          45deg,
          #2c1810,
          #2c1810 10px,
          #3a2116 10px,
          #3a2116 40px
        )`,
        opacity: 0.3,
      }
    }}>
      <Box sx={{ 
        mb: 4,
        position: 'relative',
        zIndex: 1,
      }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          align="center"
          sx={{ 
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
            color: '#faf7f2',
            mb: 4,
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
            letterSpacing: '0.05em'
          }}
        >
          音标发音训练
        </Typography>

        <Box sx={{ 
          mb: 3,
          display: 'flex', 
          justifyContent: 'center'
        }}>
          <ToggleButtonGroup
            value={pronunciationType}
            exclusive
            onChange={handlePronunciationTypeChange}
            sx={{
              '& .MuiToggleButton-root': {
                px: 4,
                py: 1.5,
                border: '2px solid #faf7f2',
                color: '#faf7f2',
                fontSize: '1rem',
                fontFamily: '"Playfair Display", serif',
                textTransform: 'none',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(250, 247, 242, 0.1)',
                },
                '&.Mui-selected': {
                  backgroundColor: '#faf7f2',
                  color: '#2c1810',
                  '&:hover': {
                    backgroundColor: '#e8e4dc',
                  }
                }
              }
            }}
          >
            <ToggleButton value="uk">英式发音</ToggleButton>
            <ToggleButton value="us">美式发音</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ 
          borderBottom: '2px solid #faf7f2',
          mb: 4
        }}>
          <Tabs 
            value={selectedTab} 
            onChange={handleTabChange} 
            centered
            sx={{
              '& .MuiTab-root': {
                fontSize: '1.1rem',
                fontWeight: 500,
                minWidth: 120,
                textTransform: 'none',
                color: '#faf7f2',
                fontFamily: '"Playfair Display", serif',
                '&.Mui-selected': {
                  color: '#faf7f2',
                  fontWeight: 700
                }
              },
              '& .MuiTabs-indicator': {
                height: 3,
                backgroundColor: '#faf7f2'
              }
            }}
          >
            <Tab label="元音" />
            <Tab label="辅音" />
          </Tabs>
        </Box>

        {renderPhonemeCards(selectedTab === 0 ? phonemes.vowels : phonemes.consonants)}
      </Box>
    </Container>
  );
}

export default App;
